import type {
  CenterLayerApiResponse,
  CenterLayerDataEntity,
} from '@/domains/centerLayer/centerLayer.entity';

/**
 * Use Case para filtrar años futuros de la data de CenterLayer
 * Cumple SRP: Solo se encarga de excluir data de años superiores al año actual
 *
 * Responsabilidades:
 * - Obtener el año actual del sistema
 * - Filtrar estructura anidada de años en cada sede
 * - Excluir cualquier año mayor al año actual
 * - Mantener integridad de la estructura de datos de la API
 */
export class FilterFutureCenterLayerDataUseCase {
  /**
   * Obtiene el año actual del sistema
   * @returns Año actual como número (ej: 2025)
   */
  private getCurrentYear(): number {
    return new Date().getFullYear();
  }

  /**
   * Filtra los años de la data de un centro específico
   * Excluye cualquier año mayor al año actual
   * @param centerData - Objeto con años como claves y su data correspondiente
   * @returns Objeto filtrado solo con años <= año actual
   */
  private filterYearsInCenterData(
    centerData: CenterLayerDataEntity,
  ): CenterLayerDataEntity {
    const currentYear = this.getCurrentYear();
    const filteredData: CenterLayerDataEntity = {};

    for (const year in centerData) {
      const yearNumber = Number.parseInt(year, 10);

      // Solo incluir años que sean <= al año actual
      if (!Number.isNaN(yearNumber) && yearNumber <= currentYear) {
        filteredData[year] = centerData[year];
      }
    }

    return filteredData;
  }

  /**
   * Filtra la respuesta completa de la API excluyendo años futuros
   * Procesa cada sede en el array de data
   * @param apiResponse - Respuesta cruda completa de la API con todas las sedes
   * @returns Respuesta filtrada con solo años <= año actual para todas las sedes
   */
  execute(apiResponse: CenterLayerApiResponse): CenterLayerApiResponse {
    // Validación de entrada
    if (!apiResponse?.data || typeof apiResponse.data !== 'object') {
      return { sede: apiResponse?.sede ?? '', data: {} };
    }

    return {
      sede: apiResponse.sede,
      data: this.filterYearsInCenterData(apiResponse.data),
    };
  }
}
